// src/app/settings/application/work-orders/assign-work-orders/work-orders-assign.component.ts

import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';

import { PlanningTopNavComponent } from '../../../../shared/planning-nav/planning-top-nav.component';

import { AssignWorkOrdersService } from './services/work-orders-assign.service';
import {
  DepartmentRow,
  UserRow,
  WorkOrderNumberRow,
  AssignResult,
} from './models/work-orders-assign.model';

type PendingAssignment = {
  employeeId: number;
  employeeName: string;
  employeeNumber: string;
  workOrderNumberId: number;
  won: string;
};

type AssignmentOutcome = PendingAssignment & {
  ok: boolean;
  statusCode?: number;
  message?: string;
};

type ConfirmRow = {
  employeeId: number;
  employeeName: string;
  employeeNumber: string;
  wons: string[]; // stacked list
};

type GroupedPendingAssignment = {
  employeeId: number;
  employeeName: string;
  employeeNumber: string;
  workOrders: Array<{ won: string; workOrderNumberId: number }>;
};

@Component({
  selector: 'app-work-orders-assign',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PlanningTopNavComponent],
  templateUrl: './work-orders-assign.component.html',
  styleUrls: ['./work-orders-assign.component.scss'],
})
export class WorkOrdersAssignComponent implements OnInit {
  // ----------------------
  // Loading / Errors
  // ----------------------
  isLoading = false;
  errorMessage: string | null = null;

  // ----------------------
  // Infinite Scroll tuning
  // ----------------------
  private readonly scrollThresholdPx = 220;

  // ----------------------
  // Data (paginated)
  // ----------------------
  workOrders: WorkOrderNumberRow[] = [];
  workOrdersPage = 1;
  workOrdersTotalPages = 1;
  workOrdersPageSize = 20;
  isLoadingWorkOrders = false;

  employees: UserRow[] = [];
  employeesPage = 1;
  employeesTotalPages = 1;
  employeesPageSize = 100;
  isLoadingEmployees = false;

  departments: DepartmentRow[] = [];
  departmentsPage = 1;
  departmentsTotalPages = 1;
  departmentsPageSize = 20;
  isLoadingDepartments = false;

  // ----------------------
  // Menus
  // ----------------------
  showDeptMenu = false;
  showWorkOrderDeptMenu = false;
  showWorkOrderYearMenu = false;

  // ----------------------
  // Employee Filters / Selection
  // ----------------------
  employeeSearch = '';
  selectedDepartmentIds: number[] = [];
  selectedEmployeeIds = new Set<number>();

  lastDraggedWorkOrder: WorkOrderNumberRow | null = null;

  // ----------------------
  // Work Order Filters
  // ----------------------
  workOrderSearch = '';
  selectedWorkOrderDepartmentIds: number[] = [];
  selectedWorkOrderYears: number[] = [];
  overheadOnly = false;

  // derived options (from loaded work orders)
  workOrderDepartments: Array<{ id: number; code: number; name: string }> = [];
  workOrderYears: number[] = [];

  // ----------------------
  // Pending batch (Save -> Confirm -> Execute)
  // ----------------------
  pendingAssignments: PendingAssignment[] = [];
  groupedPendingAssignments: GroupedPendingAssignment[] = [];

  confirmOpen = false;
  isSaving = false;

  // results (post-save)
  resultsOpen = false;
  assignmentResults: AssignmentOutcome[] = [];

  // ----------------------
  // Toast
  // ----------------------
  toastVisible = false;
  toastMessage = '';
  toastType: 'success' | 'error' = 'success';

  constructor(
    private readonly svc: AssignWorkOrdersService,
    private readonly cd: ChangeDetectorRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    this.loadInitialData();
  }

  /**
   * IMPORTANT:
   * Close menus when clicking anywhere outside.
   * HostListener keeps this in Angular’s event system (no manual document listeners).
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(_: MouseEvent): void {
    // If no menus open, do nothing (avoids extra change detection)
    if (!this.showDeptMenu && !this.showWorkOrderDeptMenu && !this.showWorkOrderYearMenu) return;

    // Close all menus. (We already stopPropagation inside the menu in HTML)
    this.showDeptMenu = false;
    this.showWorkOrderDeptMenu = false;
    this.showWorkOrderYearMenu = false;
    this.cd.detectChanges();
  }

  // ----------------------
  // Initial Load
  // ----------------------
  loadInitialData(): void {
    this.isLoading = true;
    this.errorMessage = null;

    // Kick off async loads
    this.loadMoreDepartments(true);
    this.loadMoreEmployees(true);
    this.loadMoreWorkOrders(true);

    // Keep existing behavior (your loaders have their own spinners)
    this.isLoading = false;
    this.cd.detectChanges();
  }

  // ----------------------
  // Load More (Departments)
  // ----------------------
  loadMoreDepartments(reset: boolean = false): void {
    if (this.isLoadingDepartments) return;

    if (reset) {
      this.departments = [];
      this.departmentsPage = 1;
      this.departmentsTotalPages = 1;
    }

    if (this.departmentsPage > this.departmentsTotalPages) return;

    this.isLoadingDepartments = true;

    this.svc.getDepartments(this.departmentsPage, this.departmentsPageSize, 'codeAsc').subscribe({
      next: (res) => {
        if (!res) return;

        this.departmentsTotalPages = res.totalPages;
        this.departmentsPage = res.currentPage;

        this.departments = [...this.departments, ...(res.data || [])];
        this.departmentsPage += 1;

        this.cd.detectChanges();
      },
      error: () => {
        this.showToast('Failed to load departments', 'error');
        this.cd.detectChanges();
      },
      complete: () => {
        this.isLoadingDepartments = false;
        this.cd.detectChanges();
      },
    });
  }

  // ----------------------
  // Load More (Employees)
  // ----------------------
  loadMoreEmployees(reset: boolean = false): void {
    if (this.isLoadingEmployees) return;

    if (reset) {
      this.employees = [];
      this.employeesPage = 1;
      this.employeesTotalPages = 1;
      this.selectedEmployeeIds.clear();
    }

    if (this.employeesPage > this.employeesTotalPages) return;

    this.isLoadingEmployees = true;

    this.svc.getUsers(this.employeesPage, this.employeesPageSize).subscribe({
      next: (res) => {
        if (!res) return;

        this.employeesTotalPages = res.totalPages;
        this.employeesPage = res.currentPage;

        const nextUsers = res.data || [];
        const existing = new Set(this.employees.map((u) => u.id));
        const merged = nextUsers.filter((u) => !existing.has(u.id));

        this.employees = [...this.employees, ...merged];
        this.employeesPage += 1;

        this.cd.detectChanges();
      },
      error: () => {
        this.showToast('Failed to load employees', 'error');
        this.cd.detectChanges();
      },
      complete: () => {
        this.isLoadingEmployees = false;
        this.cd.detectChanges();
      },
    });
  }

  // ----------------------
  // Load More (Work Orders)
  // ----------------------
  loadMoreWorkOrders(reset: boolean = false): void {
    if (this.isLoadingWorkOrders) return;

    if (reset) {
      this.workOrders = [];
      this.workOrdersPage = 1;
      this.workOrdersTotalPages = 1;
      this.lastDraggedWorkOrder = null;
    }

    if (this.workOrdersPage > this.workOrdersTotalPages) return;

    this.isLoadingWorkOrders = true;

    this.svc.getWorkOrderNumbers(this.workOrdersPage, this.workOrdersPageSize).subscribe({
      next: (res) => {
        if (!res) return;

        this.workOrdersTotalPages = res.totalPages;
        this.workOrdersPage = res.currentPage;

        const next = res.data || [];
        const existing = new Set(this.workOrders.map((w) => w.id));
        const merged = next.filter((w) => !existing.has(w.id));

        this.workOrders = [...this.workOrders, ...merged];
        this.workOrdersPage += 1;

        this.rebuildWorkOrderFilterOptions();
        this.cd.detectChanges();
      },
      error: () => {
        this.showToast('Failed to load work orders', 'error');
        this.cd.detectChanges();
      },
      complete: () => {
        this.isLoadingWorkOrders = false;
        this.cd.detectChanges();
      },
    });
  }

  private rebuildWorkOrderFilterOptions(): void {
    const deptMap = new Map<number, { id: number; code: number; name: string }>();
    for (const w of this.workOrders) {
      if (w.department?.id != null) {
        deptMap.set(w.department.id, {
          id: w.department.id,
          code: w.department.code,
          name: w.department.name,
        });
      }
    }
    this.workOrderDepartments = Array.from(deptMap.values()).sort((a, b) => a.code - b.code);

    const years = new Set<number>();
    for (const w of this.workOrders) {
      if (typeof w.year === 'number') years.add(w.year);
    }
    this.workOrderYears = Array.from(years.values()).sort((a, b) => b - a);

    // prune invalid selections
    if (this.selectedWorkOrderDepartmentIds.length) {
      const allowed = new Set(this.workOrderDepartments.map((d) => d.id));
      this.selectedWorkOrderDepartmentIds = this.selectedWorkOrderDepartmentIds.filter((id) =>
        allowed.has(id),
      );
    }
    if (this.selectedWorkOrderYears.length) {
      const allowedY = new Set(this.workOrderYears);
      this.selectedWorkOrderYears = this.selectedWorkOrderYears.filter((y) => allowedY.has(y));
    }
  }

  // ----------------------
  // Filtering (Employees)
  // ----------------------
  get filteredEmployees(): UserRow[] {
    const q = (this.employeeSearch || '').trim().toLowerCase();
    const deptIds = new Set(this.selectedDepartmentIds);

    return (this.employees || []).filter((u) => {
      if (deptIds.size > 0) {
        const deptId = u.department?.id ?? null;
        if (!deptId || !deptIds.has(deptId)) return false;
      }

      if (!q) return true;

      const fullName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim().toLowerCase();
      const empNum = (u.employeeNumber || '').toLowerCase();

      return fullName.includes(q) || empNum.includes(q);
    });
  }

  // ----------------------
  // Filtering (Work Orders)
  // ----------------------
  get filteredWorkOrders(): WorkOrderNumberRow[] {
    const q = (this.workOrderSearch || '').trim().toLowerCase();
    const deptIds = new Set(this.selectedWorkOrderDepartmentIds.map((x) => Number(x)));
    const years = new Set(this.selectedWorkOrderYears.map((x) => Number(x)));

    return (this.workOrders || []).filter((w) => {
      if (this.overheadOnly && !w.isOverhead) return false;

      if (deptIds.size > 0) {
        const deptId = w.department?.id ?? null;
        if (!deptId || !deptIds.has(deptId)) return false;
      }

      if (years.size > 0) {
        const y = w.year ?? null;
        if (!y || !years.has(y)) return false;
      }

      if (!q) return true;

      const won = (w.won || '').toLowerCase();
      const desc = (w.description || '').toLowerCase();

      return won.includes(q) || desc.includes(q);
    });
  }

  // ----------------------
  // Employee Department multi-select
  // ----------------------
  toggleDepartmentSelection(deptId: number): void {
    if (this.selectedDepartmentIds.includes(deptId)) {
      this.selectedDepartmentIds = this.selectedDepartmentIds.filter((id) => id !== deptId);
    } else {
      this.selectedDepartmentIds = [...this.selectedDepartmentIds, deptId];
    }

    // if you filter, clear selection to avoid invisible selections
    this.selectedEmployeeIds.clear();
    this.cd.detectChanges();
  }

  clearDepartmentFilter(): void {
    this.selectedDepartmentIds = [];
    this.selectedEmployeeIds.clear();
    this.cd.detectChanges();
  }

  // ----------------------
  // Work Order Dept filter
  // ----------------------
  toggleWorkOrderDeptSelection(deptId: number): void {
    if (this.selectedWorkOrderDepartmentIds.includes(deptId)) {
      this.selectedWorkOrderDepartmentIds = this.selectedWorkOrderDepartmentIds.filter(
        (id) => id !== deptId,
      );
    } else {
      this.selectedWorkOrderDepartmentIds = [...this.selectedWorkOrderDepartmentIds, deptId];
    }
    this.cd.detectChanges();
  }

  clearWorkOrderDeptFilter(): void {
    this.selectedWorkOrderDepartmentIds = [];
    this.cd.detectChanges();
  }

  // ----------------------
  // Work Order Year filter
  // ----------------------
  toggleWorkOrderYearSelection(year: number): void {
    if (this.selectedWorkOrderYears.includes(year)) {
      this.selectedWorkOrderYears = this.selectedWorkOrderYears.filter((y) => y !== year);
    } else {
      this.selectedWorkOrderYears = [...this.selectedWorkOrderYears, year];
    }
    this.cd.detectChanges();
  }

  clearWorkOrderYearFilter(): void {
    this.selectedWorkOrderYears = [];
    this.cd.detectChanges();
  }

  // ----------------------
  // Employee selection
  // ----------------------
  toggleEmployeeSelected(emp: UserRow): void {
    if (this.selectedEmployeeIds.has(emp.id)) this.selectedEmployeeIds.delete(emp.id);
    else this.selectedEmployeeIds.add(emp.id);

    this.cd.detectChanges();
  }

  get selectedEmployeesCount(): number {
    return this.selectedEmployeeIds.size;
  }

  isEmployeeSelected(id: number): boolean {
    return this.selectedEmployeeIds.has(id);
  }

  /**
   * Called by the Select All checkbox:
   * - checked=true  => select all currently filtered employees
   * - checked=false => unselect ONLY the currently filtered employees
   */
  toggleSelectAllFilteredEmployees(checked: boolean): void {
    const list = this.filteredEmployees;
    if (!list.length) return;

    if (!checked) {
      const filteredIds = new Set(list.map((e) => e.id));
      for (const id of Array.from(this.selectedEmployeeIds)) {
        if (filteredIds.has(id)) this.selectedEmployeeIds.delete(id);
      }
      this.cd.detectChanges();
      return;
    }

    for (const emp of list) {
      this.selectedEmployeeIds.add(emp.id);
    }
    this.cd.detectChanges();
  }

  // ----------------------
  // CLEAR BUTTONS (these must be immediate)
  // ----------------------
  clearPending(): void {
    const had = this.pendingAssignments.length;

    this.pendingAssignments = [];
    this.groupedPendingAssignments = [];

    // also close confirm/results if open (queue is now empty)
    this.confirmOpen = false;
    this.resultsOpen = false;

    this.cd.detectChanges();
    this.showToast(had ? 'Queue cleared' : 'Queue is already empty', had ? 'success' : 'error');
  }

  clearAllSelectionsAndFilters(): void {
    // employee filters + selection
    this.employeeSearch = '';
    this.selectedDepartmentIds = [];
    this.selectedEmployeeIds.clear();

    // work order filters
    this.workOrderSearch = '';
    this.selectedWorkOrderDepartmentIds = [];
    this.selectedWorkOrderYears = [];
    this.overheadOnly = false;

    // ui state
    this.lastDraggedWorkOrder = null;
    this.showDeptMenu = false;
    this.showWorkOrderDeptMenu = false;
    this.showWorkOrderYearMenu = false;

    // do NOT clear pending queue here (you have a dedicated button for that)
    this.cd.detectChanges();
    this.showToast('Selection & filters cleared', 'success');
  }

  // ----------------------
  // Infinite scroll handlers
  // ----------------------
  onEmployeesScroll(ev: Event): void {
    const el = ev.target as HTMLElement | null;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - this.scrollThresholdPx;
    if (!nearBottom) return;

    if (this.isLoadingEmployees) return;
    if (this.employeesPage > this.employeesTotalPages) return;

    this.loadMoreEmployees(false);
  }

  onWorkOrdersScroll(ev: Event): void {
    const el = ev.target as HTMLElement | null;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - this.scrollThresholdPx;
    if (!nearBottom) return;

    if (this.isLoadingWorkOrders) return;
    if (this.workOrdersPage > this.workOrdersTotalPages) return;

    this.loadMoreWorkOrders(false);
  }

  onDepartmentScroll(ev: Event): void {
    const el = ev.target as HTMLElement | null;
    if (!el) return;

    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - this.scrollThresholdPx;
    if (!nearBottom) return;

    if (this.isLoadingDepartments) return;
    if (this.departmentsPage > this.departmentsTotalPages) return;

    this.loadMoreDepartments(false);
  }

  // ----------------------
  // Drag & Drop
  // ----------------------
  onDragStartWorkOrder(workOrder: WorkOrderNumberRow): void {
    this.lastDraggedWorkOrder = workOrder;
    this.cd.detectChanges();
  }

  allowDrop(ev: DragEvent): void {
    ev.preventDefault();
  }

  onDropOnEmployee(ev: DragEvent, targetEmp: UserRow): void {
    ev.preventDefault();

    if (!this.lastDraggedWorkOrder) {
      this.showToast('Drag a work order first', 'error');
      return;
    }

    const employeesToAssign =
      this.selectedEmployeeIds.size > 0
        ? this.employees.filter((e) => this.selectedEmployeeIds.has(e.id))
        : [targetEmp];

    this.queueAssignments(this.lastDraggedWorkOrder, employeesToAssign);
  }

  onDropOnSelectedZone(ev: DragEvent): void {
    ev.preventDefault();

    if (!this.lastDraggedWorkOrder) {
      this.showToast('Drag a work order first', 'error');
      return;
    }

    if (this.selectedEmployeeIds.size === 0) {
      this.showToast('Select at least one employee first', 'error');
      return;
    }

    const employeesToAssign = this.employees.filter((e) => this.selectedEmployeeIds.has(e.id));
    this.queueAssignments(this.lastDraggedWorkOrder, employeesToAssign);
  }

  private queueAssignments(workOrder: WorkOrderNumberRow, emps: UserRow[]): void {
    const toAdd: PendingAssignment[] = emps.map((e) => ({
      employeeId: e.id,
      employeeName: `${e.firstName} ${e.lastName}`.trim(),
      employeeNumber: e.employeeNumber,
      workOrderNumberId: workOrder.id,
      won: workOrder.won,
    }));

    const key = (x: PendingAssignment) => `${x.employeeId}-${x.workOrderNumberId}`;
    const existingKeys = new Set(this.pendingAssignments.map(key));

    const merged = [...this.pendingAssignments];
    let added = 0;

    for (const item of toAdd) {
      const k = key(item);
      if (!existingKeys.has(k)) {
        merged.push(item);
        existingKeys.add(k);
        added += 1;
      }
    }

    this.pendingAssignments = merged;

    // Keep selected employees selected
    emps.forEach((e) => this.selectedEmployeeIds.add(e.id));

    // Keep grouped view in sync (especially if confirm modal is already open)
    this.buildGroupedPendingAssignments();

    this.cd.detectChanges();

    if (added === 0) this.showToast('Already queued', 'error');
    else this.showToast(`Queued ${added}`, 'success');
  }

  // ----------------------
  // Confirm Preview Grouping
  // ----------------------
  private buildGroupedPendingAssignments(): void {
    const map = new Map<number, GroupedPendingAssignment>();

    for (const item of this.pendingAssignments) {
      const existing = map.get(item.employeeId);

      if (!existing) {
        map.set(item.employeeId, {
          employeeId: item.employeeId,
          employeeName: item.employeeName,
          employeeNumber: item.employeeNumber,
          workOrders: [{ won: item.won, workOrderNumberId: item.workOrderNumberId }],
        });
        continue;
      }

      // avoid duplicate WONs per employee (based on workOrderNumberId)
      if (!existing.workOrders.some((w) => w.workOrderNumberId === item.workOrderNumberId)) {
        existing.workOrders.push({ won: item.won, workOrderNumberId: item.workOrderNumberId });
      }
    }

    this.groupedPendingAssignments = Array.from(map.values())
      .map((g) => ({
        ...g,
        workOrders: [...g.workOrders].sort((a, b) => a.won.localeCompare(b.won)),
      }))
      .sort(
        (a, b) =>
          a.employeeName.localeCompare(b.employeeName) ||
          a.employeeNumber.localeCompare(b.employeeNumber),
      );
  }

  // ----------------------
  // Confirm / Save
  // ----------------------
  openConfirm(): void {
    if (this.pendingAssignments.length === 0) {
      this.showToast('No assignments queued', 'error');
      return;
    }

    this.buildGroupedPendingAssignments();
    this.confirmOpen = true;
    this.cd.detectChanges();
  }

  closeConfirm(): void {
    this.confirmOpen = false;
    this.cd.detectChanges();
  }

  confirmAndSave(): void {
    if (this.isSaving) return;
    if (this.pendingAssignments.length === 0) return;

    this.isSaving = true;
    this.assignmentResults = [];
    this.resultsOpen = false;

    const queue = [...this.pendingAssignments];

    const runNext = (idx: number) => {
      if (idx >= queue.length) {
        this.isSaving = false;
        this.confirmOpen = false;
        this.resultsOpen = true;

        const ok = this.assignmentResults.filter((r) => r.ok).length;
        const fail = this.assignmentResults.length - ok;

        // clear queue after completion
        this.pendingAssignments = [];
        this.groupedPendingAssignments = [];

        this.cd.detectChanges();

        if (fail === 0) this.showToast(`Assigned ${ok} successfully`, 'success');
        else this.showToast(`Assigned ${ok}, ${fail} failed`, 'error');

        return;
      }

      const item = queue[idx];

      this.svc.assignUserWon(item.employeeId, item.workOrderNumberId).subscribe({
        next: (res: AssignResult) => {
          this.assignmentResults = [
            ...this.assignmentResults,
            { ...item, ok: res.ok, statusCode: res.statusCode, message: res.message },
          ];
          this.cd.detectChanges();
          runNext(idx + 1);
        },
        error: () => {
          this.assignmentResults = [
            ...this.assignmentResults,
            { ...item, ok: false, statusCode: 0, message: 'Request failed' },
          ];
          this.cd.detectChanges();
          runNext(idx + 1);
        },
      });
    };

    runNext(0);
  }

  closeResults(): void {
    this.resultsOpen = false;
    this.assignmentResults = [];
    this.cd.detectChanges();
  }

  // ----------------------
  // Toast
  // ----------------------
  showToast(message: string, type: 'success' | 'error'): void {
    this.toastMessage = message;
    this.toastType = type;
    this.toastVisible = true;

    setTimeout(() => {
      this.toastVisible = false;
      this.cd.markForCheck();
    }, 2500);
  }

  // ----------------------
  // Navigation
  // ----------------------
  backToWorkOrders(): void {
    this.router.navigate(['/settings/planning/work-orders']);
  }

  // ----------------------
  // Result counts
  // ----------------------
  get successCount(): number {
    return (this.assignmentResults || []).filter((r) => r.ok).length;
  }

  get failCount(): number {
    return (this.assignmentResults || []).filter((r) => !r.ok).length;
  }

  get confirmRows(): ConfirmRow[] {
    const map = new Map<number, ConfirmRow>();

    for (const a of this.pendingAssignments) {
      const existing = map.get(a.employeeId);
      if (!existing) {
        map.set(a.employeeId, {
          employeeId: a.employeeId,
          employeeName: a.employeeName,
          employeeNumber: a.employeeNumber,
          wons: [a.won],
        });
        continue;
      }

      // de-dupe WONs per employee (just in case)
      if (!existing.wons.includes(a.won)) existing.wons.push(a.won);
    }

    // sort wons and rows for a clean, predictable preview
    return Array.from(map.values())
      .map((r) => ({ ...r, wons: [...r.wons].sort((a, b) => a.localeCompare(b)) }))
      .sort(
        (a, b) =>
          a.employeeName.localeCompare(b.employeeName) ||
          a.employeeNumber.localeCompare(b.employeeNumber),
      );
  }
}
